/**
 * ProShip Direct v2 — add or edit one product.
 *
 * The manual counterpart to the bulk sheet, for the merchant with three SKUs to add rather than
 * three hundred. It writes through exactly the same path: the form's values go into
 * buildProductMasterCsv and up as a one-row sheet, so there is one write contract and two ways in.
 *
 * ADD AND EDIT ARE THE SAME OPERATION, because the endpoint is an upsert keyed on SKU code. The
 * only difference is that an existing product's SKU is locked — changing it would not rename the
 * product, it would quietly create a second one and leave the first behind.
 *
 * A drawer rather than a dialog: the sheet has sixteen columns plus images, and a dialog that tall
 * stops being a dialog.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Drawer, Typography, TextField, MenuItem, Button, IconButton, Divider, Tooltip,
  CircularProgress, Autocomplete,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { apiConstants, post, postGeneric } from '../../../common/constants';
import { Thumb, imagesOf } from '../../../common/ImageLightbox';
import { primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, WHITE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP } from '../../../styles/foundation/spacing';
import { MONO } from './directCellStyles';
import {
  PRODUCT_FIELD_GROUPS, buildProductMasterCsv, emptyProduct, MAX_PRODUCT_IMAGES,
} from '../data/productMaster';

const groupTitleSx = {
  fontFamily: FONT,
  fontSize: SIZE['3xs'],
  fontWeight: WEIGHT.bold,
  color: SUBTLE,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  mb: 0.75,
};
const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const sectionSx = { p: 2, borderBottom: `1px solid ${BORDER}` };

/**
 * Category and sub category are creatable pickers, not free text.
 *
 * Category offers ProShip's own list — the same {enum, displayName} vocabulary the admin panel
 * uses to set a merchant's category — so the two do not drift into separate taxonomies. Sub
 * category has no backend list, so it offers whatever the merchant already uses in their own
 * catalogue, which is the only honest source for it.
 *
 * Both accept a typed value that is not in the list. A dropdown that refuses an unlisted category
 * would block a merchant whose catalogue predates our taxonomy, and the sheet upload has never
 * enforced one — so the form should not be stricter than the file it writes.
 */
export default function ProductEditDrawer({
  product,
  merchantId,
  open,
  onClose,
  onSaved,
  categories = [],
  subCategories = [],
}) {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(product?.skuCode);

  const [values, setValues] = useState(emptyProduct);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [touchedSku, setTouchedSku] = useState(false);

  // The drawer stays mounted between rows, so the form reloads when the product changes.
  useEffect(() => {
    setValues({ ...emptyProduct(), ...(product || {}) });
    setImages(product ? imagesOf(product) : []);
    setTouchedSku(false);
  }, [product, open]);

  const set = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const skuError = touchedSku && !String(values.skuCode || '').trim()
    ? 'A SKU code is required — it is what everything matches on.'
    : null;

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (images.length >= MAX_PRODUCT_IMAGES) {
      enqueueSnackbar(`At most ${MAX_PRODUCT_IMAGES} images`, { variant: 'warning' });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('attachments[]', file);
      formData.append('folder', 'product_master');
      const res = await post(apiConstants.UPLOAD_IMAGE_NON_EXPIRE, formData);
      const url = res?.data?.[0];
      if (res.status === 200 && url) setImages((prev) => [...prev, url].slice(0, MAX_PRODUCT_IMAGES));
      else enqueueSnackbar('The upload did not return an image URL', { variant: 'error' });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Image upload failed', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setTouchedSku(true);
    if (!String(values.skuCode || '').trim()) return;

    setSaving(true);
    try {
      const csv = buildProductMasterCsv(values, images);
      const file = new File([csv], `${values.skuCode}.csv`, { type: 'text/csv' });
      const formData = new FormData();
      formData.append('skuMaster', file);
      const res = await postGeneric(
        `${apiConstants.UPLOAD_SKU_DETAILS}?merchantId=${encodeURIComponent(merchantId)}`,
        formData
      );
      if (res.status === 200) {
        enqueueSnackbar(isEdit ? 'Product updated' : 'Product added', { variant: 'success' });
        onSaved?.();
        onClose();
      } else {
        enqueueSnackbar(typeof res.data === 'string' ? res.data : 'Could not save', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Could not save', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const optionsFor = (name) => (name === "categories" ? categories : subCategories);

  const renderField = (field) => {
    const locked = isEdit && field.key === 'skuCode';
    const common = {
      size: 'small',
      fullWidth: true,
      label: field.unit ? `${field.label} (${field.unit})` : field.label,
      value: values[field.key] ?? '',
      onChange: (e) => set(field.key, e.target.value),
      sx: {
        '& .MuiInputBase-input': { fontFamily: field.mono ? MONO : FONT, fontSize: 12 },
        '& .MuiInputLabel-root': { fontSize: 10 },
      },
    };

    if (field.options) {
      const options = optionsFor(field.options);
      return (
        <Autocomplete
          freeSolo
          autoSelect
          size="small"
          options={options}
          value={values[field.key] ?? ''}
          onChange={(e, next) => set(field.key, next ?? '')}
          onInputChange={(e, next) => set(field.key, next ?? '')}
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.label}
              helperText={options.length ? field.help : "Type to add the first one"}
              FormHelperTextProps={{ sx: { fontFamily: FONT, fontSize: 10, ml: 0 } }}
              sx={{
                '& .MuiInputBase-input': { fontFamily: FONT, fontSize: 12 },
                '& .MuiInputLabel-root': { fontSize: 10 },
              }}
            />
          )}
        />
      );
    }

    if (field.select) {
      return (
        <TextField select {...common}>
          {field.select.map((option) => (
            <MenuItem key={option} value={option} sx={{ fontFamily: FONT, fontSize: 12 }}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    return (
      <TextField
        {...common}
        type={field.type || 'text'}
        disabled={locked}
        onBlur={field.key === 'skuCode' ? () => setTouchedSku(true) : undefined}
        error={field.key === 'skuCode' && Boolean(skuError)}
        helperText={
          field.key === 'skuCode'
            ? skuError || (locked ? 'Fixed once a product exists' : field.help)
            : field.help
        }
        FormHelperTextProps={{ sx: { fontFamily: FONT, fontSize: 10, ml: 0 } }}
        InputProps={
          locked
            ? {
                endAdornment: (
                  <Tooltip title='Changing a SKU code would create a second product rather than rename this one. Add a new product instead.'>
                    <LockOutlinedIcon sx={{ fontSize: 14, color: SUBTLE }} />
                  </Tooltip>
                ),
              }
            : undefined
        }
      />
    );
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: 640 }, bgcolor: WHITE } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ ...sectionSx, display: 'flex', alignItems: 'flex-start', gap: GAP.md }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY }}>
              {isEdit ? values.name || values.skuCode : 'Add a product'}
            </Typography>
            <Typography sx={smallSx}>
              {isEdit
                ? 'Changes replace this product’s row. Blank a field and it is cleared.'
                : 'Only the SKU code is required — the rest can arrive from your channel later.'}
            </Typography>
          </Box>
          <IconButton size='small' onClick={onClose} aria-label='close'>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {PRODUCT_FIELD_GROUPS.map((group) => (
            <Box key={group.key} sx={sectionSx}>
              <Typography sx={groupTitleSx}>{group.title}</Typography>
              <Grid container spacing={1.5}>
                {group.fields.map((field) => (
                  <Grid item xs={12} sm={field.width || 4} key={field.key}>
                    {renderField(field)}
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}

          <Box sx={{ p: 2 }}>
            <Typography sx={groupTitleSx}>Images</Typography>
            <Typography sx={{ ...smallSx, mb: 1 }}>
              Up to {MAX_PRODUCT_IMAGES}. Used by doorstep QC and by tracking-page recommendations.
            </Typography>
            <Grid container spacing={1}>
              {images.map((url) => (
                <Grid item key={url}>
                  <Box sx={{ position: 'relative' }}>
                    <Thumb src={url} size={64} />
                    <IconButton
                      size='small'
                      onClick={() => setImages((prev) => prev.filter((u) => u !== url))}
                      aria-label='Remove image'
                      sx={{ position: 'absolute', top: -6, right: -6, bgcolor: WHITE, boxShadow: 1, p: '2px' }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 13, color: SUBTLE }} />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
              {!images.length && (
                <Grid item xs={12}>
                  <Typography sx={smallSx}>No images yet.</Typography>
                </Grid>
              )}
            </Grid>
            <Button
              component='label'
              size='small'
              disabled={uploading || images.length >= MAX_PRODUCT_IMAGES}
              startIcon={
                uploading ? <CircularProgress size={13} /> : <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 16 }} />
              }
              sx={{ ...secondaryBtnSx, mt: 1.25 }}
            >
              {uploading ? 'Uploading…' : 'Add image'}
              <input type='file' accept='image/*' hidden onChange={pickImage} />
            </Button>
          </Box>
        </Box>

        <Divider sx={{ borderColor: BORDER }} />
        <Box sx={{ p: 2, display: 'flex', gap: GAP.md, alignItems: 'center', bgcolor: LIGHT_BG, flexWrap: 'wrap' }}>
          <LoadingButton
            loading={saving}
            variant='contained'
            size='small'
            sx={primaryBtnSx}
            onClick={save}
          >
            {isEdit ? 'Save changes' : 'Add product'}
          </LoadingButton>
          <Button size='small' sx={secondaryBtnSx} onClick={onClose}>
            Cancel
          </Button>
          <Typography sx={smallSx}>Matched on SKU code.</Typography>
        </Box>
      </Box>
    </Drawer>
  );
}
